import React from 'react';
import PropTypes from 'prop-types';
import { AppContext } from 'src/components/app-context';
import { injectScript, loadGDPRTransparencyScript } from '../../helpers';
import { getUpdates, isLoaded, get } from './helpers';
import { findPlacement } from '../../helpers/placement';
import AdContextType from '../../context/type';
import { PAGETYPE_PRODUCT } from '../../helpers/pagetype';

const PANORAMA_WIDTH = 980;
const TAKEOVER_LARGE_WIDTH = 1600;
const TAKEOVER_LARGE_HEIGHT = 120;
const TAKEOVER_BIG_L_HEIGHT = 240;

class AppNexusProvider extends React.PureComponent {
  constructor(props) {
    super(props);
    this.definedTags = [];
    this.loadedTags = [];
    this.apntag = { anq: [] };
    this.log = props.adContext.actions.log;
    this.uniqueIdCounter = 0;
    this.registeredAds = [];
    this.state = {
      context: {
        register: ad => {
          const uniqueId = this.getUniqueId();
          this.registeredAds.push({ id: uniqueId, update: ad.update });

          return uniqueId;
        },
        unregister: ad => {
          this.registeredAds = this.registeredAds.filter(
            ({ id }) => ad.id !== id
          );
        },
        update: () => {
          for (const ad of this.registeredAds) {
            ad.update();
          }
        },
        actions: {
          reload: flush => this.reload(flush),
          queue: action => this.queue(action),
          defineTag: (placement, asyncTag) =>
            this.defineTag(placement, asyncTag),
          findPlacement: placement => this.findPlacement(placement),
          getPlacementOptions: placement => this.getPlacementOptions(placement),
        },
      },
    };
    this.abortIdle = null;
  }

  componentDidMount() {
    if (window && window.requestIdleCallback) {
      const idleHandler = window.requestIdleCallback(this.init.bind(this), {
        timeout: 10000,
      });
      this.abortIdle = () => {
        window.cancelIdleCallback(idleHandler);
        this.abortIdle = null;
      };
    } else {
      this.init();
    }
  }

  componentWillUnmount() {
    if (this.abortIdle) {
      this.abortIdle();
    }

    this.queue(apntag => apntag.offEvent('adLoaded'));
    this.flush();
  }

  init() {
    const { loadGDPRScript } = this.props.config;

    injectScript(this.props.config.lib, isLoaded)
      .then(() => {
        this.log('[AppNexus] LIB LOADED');
        const queue = this.apntag.anq;
        this.apntag = get();
        queue.forEach(item => this.apntag.anq.push(item));
        this.reload(false);

        if (loadGDPRScript) {
          loadGDPRTransparencyScript();
        }
      })
      .catch(() => {
        if (window.dataLayer) {
          window.dataLayer.push({ adblock: 'active' });
        }
      });
    this.queue(apntag => {
      apntag.onEvent('adLoaded', obj => {
        this.loadedTags.push(obj);

        if (obj.targetId.includes('ad_panorama_top')) {
          const isTakeoverFormat = obj.width > PANORAMA_WIDTH;

          this.setState(prevState => ({
            ...prevState,
            context: {
              ...prevState.context,
              takeover: isTakeoverFormat
                ? {
                    width: obj.width,
                    height: obj.height,
                    offgrid: obj.width >= TAKEOVER_LARGE_WIDTH,
                    topMargin:
                      obj.width < TAKEOVER_LARGE_WIDTH
                        ? TAKEOVER_BIG_L_HEIGHT
                        : TAKEOVER_LARGE_HEIGHT,
                  }
                : undefined,
            },
          }));
        }

        this.resetTakeover();
      });
    });
  }

  queue(action) {
    this.apntag.anq.push(() => action(get()));
  }

  flush() {
    this.log('[AppNexus] FLUSH');
    this.queue(apntag => apntag.clearRequest());
    this.uniqueIdCounter %= 10000;
  }

  reload(flush = true) {
    // Use flush to reload all ads on page
    if (flush) {
      this.flush();
    }
    // Use this hack to ensure AdContainers are rendered/updated
    window.requestAnimationFrame(() => {
      this.loadTags();
    });
  }

  static getReferrer() {
    const searchQuery = window?.location?.search;
    if (!searchQuery) {
      return undefined;
    }

    const searchParams = new URLSearchParams(searchQuery);
    return searchParams.get('ad');
  }

  getPageOpts() {
    const {
      market,
      experiments = [],
      state: {
        siteMode,
        siteName,
        pageType,
        pathChain,
        brand,
        priceData,
        schPageType,
        searchQuery = '',
        view,
      },
    } = this.props.adContext;
    const { pageOpts } = this.props.config;
    const { location, screen, innerHeight, innerWidth } = window;
    const adTestMode = location.search
      ? location.search.indexOf('adtest=1') > -1
      : false;
    const test = adTestMode ? '-Test' : '';
    const publisher = `${market.toUpperCase()}-${siteName}${test}`;
    const supplyType = siteMode === 'desktop' ? 'web_desktop' : 'web_phone';
    const pagegen = ['mobile', 'desktop'].includes(this.context.renderMode)
      ? 'classic'
      : this.context.market.ads.pageGen;
    const { ads } = this.context.market;
    if (ads && ads.memberIdOverride) {
      pageOpts.member = ads.memberIdOverride;
    }
    const referrer = AppNexusProvider.getReferrer();
    const pageTypeSearch = view === 'search' ? 'search' : undefined;

    const brandKeywordKey =
      market === 'no' ? 'no-sno-prisjakt-brand' : 'se-generic-brand';

    let searchQueryParam = searchQuery;

    if (pageTypeSearch) {
      const searchParams = new URLSearchParams(window?.location?.search);
      searchQueryParam = searchParams.get('search') || '';
    }

    return {
      ...pageOpts,
      keywords: {
        ...pageOpts.keywords,
        'aa-sch-page_type': pageTypeSearch || schPageType,
        'aa-sch-supply_type': supplyType,
        'aa-sch-country_code': adTestMode ? 'te' : market,
        'aa-sch-publisher': publisher,
        'se-generic-pagegen': pagegen,
        'se-generic-appmode': 'notinapp',
        'se-generic-screenheight': screen.height,
        'se-generic-screenwidth': screen.width,
        'se-generic-viewportheight': innerHeight,
        'se-generic-viewportwidth': innerWidth,
        'se-generic-sitemode': siteMode,
        'se-generic-page': pageTypeSearch || pageType,
        'se-generic-price': priceData,
        'se-generic-experiments': experiments
          .map(experiment => `${experiment.id}:${experiment.variant}`)
          .join(','),
        'se-generic-section': pathChain,
        'se-generic-searchword': searchQueryParam,
        'se-prisjakt-section':
          pageType === PAGETYPE_PRODUCT &&
          Array.isArray(pathChain) &&
          pathChain.length
            ? pathChain
                .slice(0, -1)
                .concat(`p${pathChain[pathChain.length - 1]}`)
            : pathChain,
        ...(referrer && { 'se-generic-test': referrer }),
        ...(brand &&
          brand.name && { [brandKeywordKey]: brand.name.toLowerCase() }),
        ...(market === 'no' && {
          'NO-SNO-Prisjakt-Page': pageType,
          'NO-SNO-Prisjakt-Section': pathChain,
        }),
      },
    };
  }

  /**
   * A counter to give each ad placement a unique (HTML element) ID.
   */
  getUniqueId() {
    return this.uniqueIdCounter++; // eslint-disable-line no-plusplus
  }

  findPlacement(placement) {
    const { state } = this.props.adContext;
    const key = findPlacement(
      state.placementsMap,
      placement,
      state.pathChain,
      state.pageType,
      state.siteMode
    );
    this.log('[AppNexus] LOOKING FOR PLACEMENT', placement, key);
    const placementOptions = key
      ? {
          ...state.placementsMap[key],
          ...this.getPlacementOptions(placement),
          key,
          containerId: `${
            state.placementsMap[key].containerId
          }-${this.getUniqueId()}`,
        }
      : null;
    return placementOptions;
  }

  getPlacementOptions(placement) {
    const { placementOptions } = this.props.config;
    return placementOptions && placementOptions[placement]
      ? placementOptions[placement]
      : {};
  }

  defineTag(placement, asyncTag = false) {
    this.log('[AppNexus] DEFINE TAG', placement);
    this.definedTags.push(placement);

    if (asyncTag) {
      this.loadTags();
    }
  }

  loadTags() {
    const { market } = this.props.adContext;
    const { allowedFormats } = this.props.config;
    const pageOpts = this.getPageOpts();

    this.queue(apntag => {
      this.log('[AppNexus] LOAD TAGS', this.definedTags);

      apntag.setPageOpts(pageOpts);

      this.definedTags.forEach(tag => {
        const { containerId, placementCode, sizes, formats } = tag;

        const adSizes = sizes;

        apntag.defineTag({
          invCode: placementCode,
          targetId: containerId,
          allowedFormats,
          sizes,
          keywords: {
            'se-generic-adformat': formats,
            'se-generic-adsize': adSizes.map(size => size.join('x')),
            ...(market === 'no' && {
              'NO-SNO-Adformat': formats,
            }),
          },
        });
      });

      apntag.loadTags();

      this.definedTags = [];
      this.loadedTags = [];
    });
  }

  resetTakeover() {
    const panoramaTopElm = document.querySelector('*[id^=ad_panorama_top]');

    if (!panoramaTopElm || panoramaTopElm.clientWidth <= 980) {
      this.setState(prevState => ({
        ...prevState,
        context: {
          ...prevState.context,
          takeover: undefined,
        },
      }));
    }
  }

  componentDidUpdate(prevProps) {
    const updates = getUpdates(
      prevProps.adContext.state,
      this.props.adContext.state,
      ['pathKey', 'pageType', 'siteMode', 'view']
    );
    if (Object.values(updates).some(item => !!item)) {
      const onlyViewUpdated = Object.keys(updates).every(
        key => !updates[key] || (key === 'view' && updates.view)
      );
      this.reload(!onlyViewUpdated);
    }
  }

  render() {
    const BaseProvider = this.props.baseProvider;
    return (
      <BaseProvider value={this.state.context}>
        {this.props.children}
      </BaseProvider>
    );
  }
}

const { element, object, func, shape } = PropTypes;

AppNexusProvider.propTypes = {
  adContext: AdContextType.isRequired,
  baseProvider: object.isRequired,
  config: object.isRequired,
  children: element.isRequired,
};

AppNexusProvider.contextType = AppContext;

export const AppNexusContextType = shape({
  actions: shape({
    queue: func,
    defineTag: func,
    findPlacement: func,
    getPlacementOptions: func,
  }),
});

export default AppNexusProvider;
